import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PencomCard } from "@/components/cards/PencomCard";
import { PencomRoutes } from "@/router/PencomRoutes";
import { EContributionType } from "@/constants/enums/contribution-type.enum";
import { cn } from "@/utils/cn";
import ComplianceService from "@/services/compliance/compliance.service";
import { useAuthStore } from "@/stores/authStore";
import { DataTable } from "@/components/common/DataTable";
import type { TableColumn } from "@/types";
import { formatNGN } from "@/utils/moneyFormatter";
import { normalizeCurrentYearReportResponse } from "@/utils/pccCertificateAggregates";
import { PageSkeleton } from "@/components/common/SkeletonLoader";
import { MonthlyContributionChart } from "@/components/dashboard/MonthlyContributionChart";
import {
  CONTRIBUTION_TYPE_LABEL,
  getMonthlyBreakdownSummary,
  parseMonthLocal,
  toMonthKey,
  yearsFromIncorporation,
} from "./contributionHistory.utils";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { useDownloadContributionsScheduleXlsx } from "@/hooks/compliance/compliance.hook";
import { toast } from "@/utils/toast";

interface MonthlyBreakdown {
  month: string;
  contributionType: string | null;
  totalContribution: number | null;
  employeeCount: number | null;
  avgContribution: number;
  hasContribution: boolean;
}

interface ApiMonthlyRow {
  month: string;
  contributionType?: string | null;
  totalContribution?: unknown;
  employeeCount?: unknown;
  avgContribution?: unknown;
}

function makePlaceholderRow(
  selectedYear: number,
  monthIndex: number
): MonthlyBreakdown {
  return {
    month: toMonthKey(selectedYear, monthIndex),
    contributionType: null,
    totalContribution: null,
    employeeCount: null,
    avgContribution: 0,
    hasContribution: false,
  };
}

/**
 * Group the API's monthly rows by calendar month. A single month can carry
 * several rows (e.g. a COM contribution and a PEN penalty), so entries are
 * collected into an array per month rather than overwriting one another.
 */
function groupApiRowsByMonth(
  report: Record<string, unknown> | undefined
): Map<number, MonthlyBreakdown[]> {
  const mb = report?.monthlyBreakdown;
  const rows = Array.isArray(mb) ? (mb as ApiMonthlyRow[]) : [];
  const byMonth = new Map<number, MonthlyBreakdown[]>();

  rows.forEach((row) => {
    const parsedDate = parseMonthLocal(row.month);
    if (Number.isNaN(parsedDate.getTime())) return;
    const monthIndex = parsedDate.getMonth();
    const entry: MonthlyBreakdown = {
      month: row.month,
      contributionType: row.contributionType ?? null,
      totalContribution:
        typeof row.totalContribution === "number" ? row.totalContribution : null,
      employeeCount:
        typeof row.employeeCount === "number" ? row.employeeCount : null,
      avgContribution:
        typeof row.avgContribution === "number" ? row.avgContribution : 0,
      hasContribution: true,
    };
    const existing = byMonth.get(monthIndex);
    if (existing) existing.push(entry);
    else byMonth.set(monthIndex, [entry]);
  });

  return byMonth;
}

/**
 * Table rows for the selected contribution type: one row per calendar month,
 * showing that month's matching entry (its COM or PEN row) or a placeholder
 * when the month has no entry of that type.
 */
function buildMonthlyTableRows(
  report: Record<string, unknown> | undefined,
  selectedYear: number,
  contributionType: string
): MonthlyBreakdown[] {
  const byMonth = groupApiRowsByMonth(report);
  return Array.from({ length: 12 }, (_, monthIndex) => {
    const match = byMonth
      .get(monthIndex)
      ?.find((entry) => entry.contributionType === contributionType);
    return match ?? makePlaceholderRow(selectedYear, monthIndex);
  });
}

/**
 * Chart points: one bar per calendar month, summing every remittance that
 * month (contributions + penalties) so multi-type months aren't reduced to a
 * single type's amount. Employee count uses the largest of the month's rows to
 * avoid double-counting the same headcount across types.
 */
function buildMonthlyChartData(
  report: Record<string, unknown> | undefined,
  selectedYear: number
): MonthlyBreakdown[] {
  const byMonth = groupApiRowsByMonth(report);
  return Array.from({ length: 12 }, (_, monthIndex) => {
    const entries = byMonth.get(monthIndex);
    if (!entries || !entries.length) {
      return makePlaceholderRow(selectedYear, monthIndex);
    }
    const totalContribution = entries.reduce(
      (sum, entry) => sum + (entry.totalContribution ?? 0),
      0
    );
    const employeeCount = entries.reduce(
      (max, entry) => Math.max(max, entry.employeeCount ?? 0),
      0
    );
    return {
      month: entries[0].month,
      contributionType: null,
      totalContribution,
      employeeCount: employeeCount || null,
      avgContribution: 0,
      hasContribution: true,
    };
  });
}

export const ContributionHistoryPage = () => {
  const navigate = useNavigate();
  const currentEmployer = useAuthStore((s) => s.currentEmployer);
  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth();
  const [selectedYear, setSelectedYear] = useState(() => currentYear);
  const [selectedType, setSelectedType] = useState<EContributionType>(
    EContributionType.CONTRIBUTION
  );

  const {
    mutateAsync: downloadContributionsScheduleXlsx,
    isPending: isDownloadingContributionsScheduleXlsx,
  } = useDownloadContributionsScheduleXlsx();

  const yearOptions = useMemo(() => {
    const incorporationDate = currentEmployer?.incorporationDate;
    if (!incorporationDate) return [currentYear];
    return yearsFromIncorporation(incorporationDate, currentYear);
  }, [currentEmployer?.incorporationDate, currentYear]);

  useEffect(() => {
    if (yearOptions.length === 0) return;
    if (!yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions, selectedYear]);

  const { data: yearReportRaw, isPending } = useQuery({
    queryKey: ["current-year-reports", { year: selectedYear }],
    queryFn: () => ComplianceService.getCurrentYearReports({ year: selectedYear }),
    staleTime: 5 * 60 * 1000,
  });

  const report = useMemo(
    () => normalizeCurrentYearReportResponse(yearReportRaw),
    [yearReportRaw]
  );

  const summary = useMemo(() => getMonthlyBreakdownSummary(report), [report]);

  const tableData = useMemo(
    () => buildMonthlyTableRows(report, selectedYear, selectedType),
    [report, selectedYear, selectedType]
  );
  const chartData = useMemo(
    () => buildMonthlyChartData(report, selectedYear),
    [report, selectedYear]
  );

  // For the current year, only show months up to the present one.
  const isVisibleMonth = (monthIso: string) => {
    if (selectedYear !== currentYear) return true;
    return parseMonthLocal(monthIso).getMonth() <= currentMonthIndex;
  };

  const visibleTableData = useMemo(
    () => tableData.filter((row) => isVisibleMonth(row.month)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableData, currentMonthIndex, currentYear, selectedYear]
  );
  const visibleChartData = useMemo(
    () => chartData.filter((row) => isVisibleMonth(row.month)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartData, currentMonthIndex, currentYear, selectedYear]
  );

  const handleRowClick = (row: MonthlyBreakdown) => {
    if (!row.hasContribution) return;
    const date = parseMonthLocal(row.month);
    const path = PencomRoutes.CONTRIBUTION_BY_MONTH.replace(
      ":year",
      String(date.getFullYear())
    ).replace(":month", String(date.getMonth() + 1));
    const qs = row.contributionType
      ? `?${new URLSearchParams({ contributionType: row.contributionType })}`
      : "";
    navigate(`${path}${qs}`);
  };

  const handleDownloadContributionSchedule = async () => {
    try {
      const blob = await downloadContributionsScheduleXlsx();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Contributions-Schedule.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(
        "Could not download the contributions schedule. Please try again."
      );
    }
  };

  const columns: TableColumn<MonthlyBreakdown>[] = useMemo(
    () => [
      {
        key: "month",
        header: "PERIOD",
        render: (value) => (
          <span className="text-medium-dark-grey font-regular">
            {parseMonthLocal(value as string).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </span>
        ),
      },
      {
        key: "totalContribution",
        header: "AMOUNT",
        render: (value, row) => {
          if (typeof value === "number") {
            return (
              <span className="text-black-dark-grey font-semibold">
                {formatNGN(value)}
              </span>
            );
          }
          const monthDate = parseMonthLocal(row.month);
          const isFutureMonthInCurrentYear =
            selectedYear === currentYear &&
            monthDate.getFullYear() === currentYear &&
            monthDate.getMonth() > currentMonthIndex;
          // "NOT REMITTED" only makes sense for contributions — a month with
          // no penalty is not a missed remittance.
          const shouldShowNotRemitted =
            selectedType === EContributionType.CONTRIBUTION &&
            selectedYear < currentYear &&
            !isFutureMonthInCurrentYear;
          return (
            <span
              className={`font-semibold ${
                shouldShowNotRemitted ? "text-dark-red" : "text-black-dark-grey"
              }`}
            >
              {shouldShowNotRemitted ? "NOT REMITTED" : "-"}
            </span>
          );
        },
      },
      {
        key: "employeeCount",
        header: "NUMBER OF EMPLOYEES",
        render: (value) => (
          <span className="text-medium-dark-grey font-regular">
            {typeof value === "number" ? value : "-"}
          </span>
        ),
      },
      {
        key: "contributionType",
        header: "TYPE",
        render: (value) => (
          <span className="text-medium-dark-grey font-medium">
            {CONTRIBUTION_TYPE_LABEL[value as string] ?? "-"}
          </span>
        ),
      },
    ],
    [currentMonthIndex, currentYear, selectedYear, selectedType]
  );

  if (isPending) {
    return <PageSkeleton tableColumns={4} tableRows={6} />;
  }

  const summaryItems = summary
    ? [
        { label: "Total Contribution", value: formatNGN(summary.totalContribution) },
        { label: "Total Transactions", value: summary.totalTransactions },
        { label: "Avg Monthly Employees", value: summary.avgMonthlyEmployees },
        { label: "Max Monthly Employees", value: summary.employeeCount },
      ]
    : [];

  return (
    <div className="flex min-h-screen w-full flex-col">
      <div className="mx-auto mt-6 flex w-full max-w-[1240px] flex-col px-4">
        <div className="mb-6 flex flex-row flex-wrap items-start justify-between gap-4">
          <p className="font-clash-display text-2xl font-semibold text-medium-dark-grey">
            Contribution History
          </p>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <PrimaryButton
              title="Download Contribution Schedule"
              isLoading={isDownloadingContributionsScheduleXlsx}
              onClick={handleDownloadContributionSchedule}
              disabled={isDownloadingContributionsScheduleXlsx}
            />
            <div className="flex items-center gap-x-2">
            <label
              htmlFor="year-filter"
              className="text-sm font-medium text-medium-dark-grey"
            >
              Year:
            </label>
            <select
              id="year-filter"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="cursor-pointer rounded-lg border border-stroke bg-white px-3 py-2 font-clash-display text-sm font-medium text-medium-dark-grey focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-button"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            </div>
          </div>
        </div>

        {summaryItems.length > 0 && (
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            {summaryItems.map(({ label, value }) => (
              <div
                key={label}
                className="rounded-lg border border-stroke bg-white p-4"
              >
                <p className="mb-1 text-xs font-regular text-medium-dark-grey">
                  {label}
                </p>
                <p className="font-clash-display text-xl font-semibold text-black-dark-grey">
                  {value}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="mb-6">
          <MonthlyContributionChart data={visibleChartData} />
        </div>

        <div className="mb-6">
          <PencomCard>
            <div className="flex justify-end">
              <div
                role="tablist"
                aria-label="Breakdown type"
                className="inline-flex rounded-lg border border-stroke bg-off-white p-1"
              >
                {[
                  EContributionType.CONTRIBUTION,
                  EContributionType.PENALTY,
                ].map((type) => (
                  <button
                    key={type}
                    type="button"
                    role="tab"
                    aria-selected={selectedType === type}
                    onClick={() => setSelectedType(type)}
                    className={cn(
                      "cursor-pointer rounded-md px-4 py-1.5 font-clash-display text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-button",
                      selectedType === type
                        ? "bg-primary-button text-white"
                        : "text-medium-grey hover:text-medium-dark-grey"
                    )}
                  >
                    {CONTRIBUTION_TYPE_LABEL[type]}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-6">
              <DataTable
                data={visibleTableData}
                columns={columns}
                currentPage={1}
                totalPages={1}
                onPageChange={() => {}}
                showPagination={false}
                emptyMessage="No Remittance History Available"
                onRowClick={handleRowClick}
              />
            </div>
          </PencomCard>
        </div>
      </div>
    </div>
  );
};
